import { Config } from '@otp/icons';
import { Card, Typography } from '@otp/ui';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
  Actions,
  AppArea,
  Subjects,
  User,
  UserDetailsDocument,
} from 'src/graphql/generated/graphql';
import { useUserDisablePortalAccess } from 'src/hooks/query/users/use-user-disable-portal-access';
import { useUserEnablePortalAccess } from 'src/hooks/query/users/use-user-enable-portal-access';
import { Can } from 'src/utils/can';

type UserAccessToggleCardProps = {
  user: Partial<User>;
  className?: string;
};

export const UserAccessToggleCard = ({
  user,
  className,
}: UserAccessToggleCardProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'manage-otps.actions',
  });

  const [enableUserPortalAccess, { loading: enableUserLoading }] =
    useUserEnablePortalAccess(user.id, [UserDetailsDocument]);

  const [disableUserPortalAccess, { loading: disableUserLoading }] =
    useUserDisablePortalAccess(user.id, [UserDetailsDocument]);

  const handleDisablePortalAccess = async () => {
    await disableUserPortalAccess();
  };

  const handleEnablePortalAccess = async () => {
    await enableUserPortalAccess();
  };

  const resolvedClassName = clsx(
    'hover:bg-[var(--mui-palette-primary-200)] cursor-pointer',
    className,
  );

  return user.hasPortalAccess ? (
    <Can I={Actions.Disable} a={Subjects.Users} appArea={AppArea.Admin}>
      <Card className={resolvedClassName} onClick={handleDisablePortalAccess}>
        <div className="grid grid-cols-12 items-center">
          <Typography variant="subtitle2" className="col-span-11">
            {t('disable-portal-access')}
          </Typography>
          <Config />
        </div>
      </Card>
    </Can>
  ) : (
    <Can I={Actions.Enable} a={Subjects.Users} appArea={AppArea.Admin}>
      <Card className={resolvedClassName} onClick={handleEnablePortalAccess}>
        <div className="grid grid-cols-12 items-center">
          <Typography variant="subtitle2" className="col-span-11">
            {t('enable-portal-access')}
          </Typography>
          <Config />
        </div>
      </Card>
    </Can>
  );
};
